<template>
  <v-container fluid class="px-lg-5">
    <app-header
      :title="title"
      :breadcrumbs="breadcrumbs"
      :actionButton="actionButton"
      @clicked="goToCreateForm"
      :showButton="true"
    />
    <v-card class="rounded-card" flat outlined>
      <v-data-table
        :headers="headers"
        :items="appraisalTemplates.items"
        :options.sync="options"
        @update:options="updateOptions"
        :server-items-length="appraisalTemplates.count"
        :loading="loading"
        :no-data-text="$t('messages.emptyState', { entity: $tc('models.appraisalTemplate.entity') })"
        :no-results-text="$t('messages.emptyState', { entity: $tc('models.appraisalTemplate.entity') })"
        flat
      >
        <template v-slot:[`item.name`]="{ item }">
          {{ item.name }}
        </template>
        <template v-slot:[`item.createdById`]="{ item }">
          {{ createdByName(item.createdById) }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip small :color="statusColors[item.status]" text-color="white">
            {{ $t(`models.appraisalTemplate.attributes.statuses.${item.status}`) }}
          </v-chip>
        </template>
        <template v-slot:[`item.questionsCount`]="{ item }">
          {{ item.questionsCount }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small @click="viewTemplate(item)" v-on="on" v-bind="attrs">
                <v-icon>mdi-eye-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('actions.view') }}</span>
          </v-tooltip>
          <v-tooltip v-if="isPublishable(item)" top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small @click="openPublishDialog(item)" v-on="on" v-bind="attrs">
                <v-icon>mdi-publish</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('actions.publish') }}</span>
          </v-tooltip>
          <v-tooltip v-if="item.canEdit" top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small @click="editTemplate(item)" v-on="on" v-bind="attrs">
                <v-icon>mdi-pen</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('actions.edit') }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small @click="duplicateTemplate(item)" v-on="on" v-bind="attrs">
                <v-icon>mdi-content-copy</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('actions.duplicate') }}</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small @click="deleteTemplate(item)" v-on="on" v-bind="attrs">
                <v-icon>mdi-trash-can-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('actions.delete') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="publishDialog.show" max-width="500">
      <v-card>
        <v-card-title>{{ $t('views.appraisals.list.publishTo') }}</v-card-title>
        <v-card-text>
          <div v-if="publishDialog.loadingUsers" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <template v-else>
            <div v-if="!availableUsers.length" class="text-body-1 grey--text">
              {{ $t('views.appraisals.list.noUsers') }}
            </div>
            <v-checkbox v-for="user in availableUsers" :key="user.id" :label="user.name" :value="`${user.id}`"
              v-model="publishDialog.selectedUserIds" hide-details dense class="mt-0" />
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="publishDialog.show = false">{{ $t('actions.cancel') }}</v-btn>
          <v-btn color="primary" :loading="publishDialog.submitting"
            :disabled="!publishDialog.selectedUserIds.length || publishDialog.submitting" @click="confirmPublish">
            {{ $t('actions.publish') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {appraisalTemplateStatusColors, appraisalTemplateStatuses} from 'constants';

export default {
  name: 'AppraisalTemplatesList',
  data() {
    return {
      loading: false,
      statusColors: appraisalTemplateStatusColors,
      title: {
        text: this.$t('layout.mainMenu.appraisalsList'),
        icon: 'mdi-format-list-bulleted'
      },
      breadcrumbs: [
        {
          text: this.$t('layout.mainMenu.home'),
          to: {name: 'Home'}
        },
        {
          text: this.$t('layout.mainMenu.appraisals'),
          disabled: true
        },
        {
          text: this.$t('layout.mainMenu.appraisalsList'),
          to: {name: 'AppraisalTemplates'},
          exact: true
        }
      ],
      actionButton: {
        text: this.$t('actions.create', {entity: this.$tc('models.appraisalTemplate.entity', 1)}),
        icon: 'mdi-plus'
      },
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [true],
        mustSort: ['true'],
        multiSort: false
      },
      headers: [
        {
          text: this.$t('models.appraisalTemplate.attributes.title'),
          value: 'name',
          sortable: false
        },
        {
          text: this.$t('models.appraisalTemplate.attributes.createdBy'),
          value: 'createdById',
          sortable: false
        },
        {
          text: this.$t('models.appraisalTemplate.attributes.status'),
          value: 'status',
          sortable: false
        },
        {
          text: this.$t('models.appraisalTemplate.attributes.questionsCount'),
          value: 'questionsCount',
          sortable: false
        },
        {
          text: '',
          value: 'actions',
          sortable: false,
          width: '200px'
        }
      ],
      publishDialog: {
        show: false,
        template: null,
        selectedUserIds: [],
        assignedUserIds: [],
        loadingUsers: false,
        submitting: false
      }
    };
  },
  computed: {
    ...mapGetters(['appraisalTemplates', 'users']),
    availableUsers() {
      return this.users.items.filter((user) => !this.publishDialog.assignedUserIds.includes(`${user.id}`));
    }
  },
  methods: {
    ...mapActions([
      'getAppraisalTemplates', 'getUsers', 'deleteAppraisalTemplate', 'publishAppraisalTemplate', 'assignAppraisalTemplate',
      'getAssignedEmployeeIds', 'duplicateAppraisalTemplate'
    ]),
    updateOptions(options) {
      this.loading = true;
      this.getAppraisalTemplates(options).then(() => (this.loading = false));
    },
    createdByName(userId) {
      const user = this._.find(this.users.items, (user) => `${user.id}` === `${userId}`);
      return user ? user.name : '';
    },
    goToCreateForm() {
      this.$router.push({name: 'CreateAppraisalForm'});
    },
    isPublishable(template) {
      return template.status !== appraisalTemplateStatuses.ARCHIVED;
    },
    editTemplate(template) {
      this.$router.push({name: 'EditAppraisalForm', params: {id: template.id}});
    },
    viewTemplate(template) {
      this.$router.push({name: 'ViewAppraisalForm', params: {id: template.id}});
    },
    duplicateTemplate(template) {
      this.duplicateAppraisalTemplate(template.id).then((response) => {
        if (this.successCode(response.status)) {
          this.updateOptions(this.options);
        } else {
          this.displayServerErrorsInToaster(response.errors);
        }
      });
    },
    deleteTemplate(template) {
      this.$confirm(this.$t('messages.confirmation.delete', {entity: template.name}), {
        buttonTrueText: this.$t('actions.confirm'),
        buttonFalseText: this.$t('actions.cancel')
      }).then((confirmed) => {
        if (confirmed) {
          this.deleteAppraisalTemplate(template.id).then((response) => {
            if (this.successCode(response.status)) {
              this.updateOptions(this.options);
            }
          });
        }
      });
    },
    async openPublishDialog(template) {
      this.publishDialog.template = template;
      this.publishDialog.selectedUserIds = [];
      this.publishDialog.assignedUserIds = [];
      this.publishDialog.show = true;
      this.publishDialog.loadingUsers = true;
      this.publishDialog.assignedUserIds = await this.getAssignedEmployeeIds(template.id);
      this.publishDialog.loadingUsers = false;
    },
    async confirmPublish() {
      this.publishDialog.submitting = true;
      const template = this.publishDialog.template;
      if (template.status === appraisalTemplateStatuses.DRAFT) {
        const publishResponse = await this.publishAppraisalTemplate(template.id);
        if (!this.successCode(publishResponse.status)) {
          this.displayServerErrorsInToaster(publishResponse.errors);
          this.publishDialog.submitting = false;
          return;
        }
        this.$snotify.success(this.$t('views.appraisals.list.publishSuccess', {name: template.name}));
        this.updateOptions(this.options);
      }
      const assignResponse = await this.assignAppraisalTemplate({
        appraisalTemplateId: template.id,
        employeeIds: this.publishDialog.selectedUserIds
      });
      if (this.successCode(assignResponse.status)) {
        this.$snotify.success(this.$t('views.appraisals.list.assignSuccess', {
          count: this.publishDialog.selectedUserIds.length
        }));
        this.publishDialog.show = false;
      } else {
        this.$snotify.error(this.$t('views.appraisals.list.assignFailed'));
        this.displayServerErrorsInToaster(assignResponse.errors);
      }
      this.publishDialog.submitting = false;
      this.updateOptions(this.options);
    }
  },
  created() {
    this.getUsers({fields: ['firstName', 'lastName']});
  }
};
</script>
